import { useCallback, useEffect, useMemo, useState } from "react";

export interface SelectionRect {
  r0: number;
  r1: number;
  c0: number;
  c1: number;
}

export function rectContains(rect: SelectionRect, r: number, c: number): boolean {
  return r >= rect.r0 && r <= rect.r1 && c >= rect.c0 && c <= rect.c1;
}

// Excel-style rectangular cell selection for the Bottom Up grids.
//
// Click a cell to anchor; Shift+Click another to extend the rectangle. When the
// selection spans more than one cell, Delete/Backspace clears the range (the
// owner translates the rect into cells and writes 0); Escape collapses it.
//
// A single-cell selection is intentionally left alone so normal in-cell editing
// (Backspace deletes a digit) keeps working — we only intercept keys once a
// multi-cell range exists.
//
// Edit mode vs selection mode: once the user has typed into a cell (the input
// marks itself data-dirty), Backspace/Delete go back to editing characters —
// clearing the range while someone is mid-typing would destroy their entry.
export function useGridSelection(onDeleteRange: (rect: SelectionRect) => void) {
  const [anchor, setAnchor] = useState<{ r: number; c: number } | null>(null);
  const [focus, setFocus] = useState<{ r: number; c: number } | null>(null);

  const rect = useMemo<SelectionRect | null>(() => {
    if (!anchor || !focus) return null;
    return {
      r0: Math.min(anchor.r, focus.r),
      r1: Math.max(anchor.r, focus.r),
      c0: Math.min(anchor.c, focus.c),
      c1: Math.max(anchor.c, focus.c),
    };
  }, [anchor, focus]);

  const multi = !!rect && (rect.r0 !== rect.r1 || rect.c0 !== rect.c1);

  // Highlight only a genuine multi-cell range; a single anchored cell is already
  // indicated by its own focus border, so tinting it too would be noise.
  const isSelected = useCallback(
    (r: number, c: number) =>
      multi && !!rect && r >= rect.r0 && r <= rect.r1 && c >= rect.c0 && c <= rect.c1,
    [multi, rect],
  );

  const onCellMouseDown = useCallback(
    (r: number, c: number, shiftKey: boolean) => {
      if (shiftKey && anchor) {
        setFocus({ r, c });
      } else {
        setAnchor({ r, c });
        setFocus({ r, c });
      }
    },
    [anchor],
  );

  const clear = useCallback(() => {
    setAnchor(null);
    setFocus(null);
  }, []);

  useEffect(() => {
    if (!multi || !rect) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const editingCell = target instanceof HTMLInputElement && target.dataset.dirty === "true";
      if ((e.key === "Delete" || e.key === "Backspace") && !editingCell) {
        e.preventDefault();
        onDeleteRange(rect);
      } else if (e.key === "Escape") {
        clear();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [multi, rect, onDeleteRange, clear]);

  // Clicking anywhere outside the grid cells deselects. Cell clicks are left
  // to the cell's own mousedown (re-anchor or shift-extend), and when a typed
  // anchor is about to blur-fill the selection we must not clear early — the
  // fill's commit path clears it afterwards.
  useEffect(() => {
    if (!multi) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest("input[data-row]")) return;
      const ae = document.activeElement;
      if (ae instanceof HTMLInputElement && ae.dataset.dirty === "true") return;
      clear();
    };
    window.addEventListener("mousedown", onMouseDown);
    return () => window.removeEventListener("mousedown", onMouseDown);
  }, [multi, clear]);

  return { isSelected, onCellMouseDown, clear, multi, rect };
}

// Ring drawn around a selected input (single box-shadow layer, no background
// fill). Kept local to the grids so it can't ripple into other dense layouts.
export const SELECTED_CELL_RING = "0 0 0 1.5px var(--primary)";
